import { useState, useEffect, KeyboardEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  CircularProgress,
  makeStyles,
  List,
  ListItem,
  ListItemText,
  Chip,
  Collapse,
  FormControlLabel,
  Checkbox,
  Tooltip,
  TextField,
  Tabs,
  Tab,
  Snackbar,
  SnackbarContent,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import StarIcon from '@material-ui/icons/Star';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { WhatsNewRelease, MajorReleaseGroup } from './githubReleasesService';
import idpLogo from '../Root/logo/idp-logo.svg';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Allow only safe URL schemes so markdown links can't smuggle javascript: etc.
const SAFE_URL_SCHEMES = ['http', 'https', 'mailto', 'tel'];
export const sanitizeMarkdownUrl = (url: string): string => {
  const value = String(url || '').trim();
  if (!value) return '';
  const scheme = value.match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase();
  if (!scheme) return value; // relative URL / fragment
  return SAFE_URL_SCHEMES.includes(scheme) ? value : '';
};

const markdownComponents = {
  // Links open in a new tab and never leak the opener.
  a: ({ children, ...props }: any) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

/** Renders user/admin markdown with GFM autolinks and sanitized, clickable links. */
const SafeMarkdown = ({ children }: { children: string }) => (
  <ReactMarkdown
    skipHtml
    remarkPlugins={[remarkGfm]}
    urlTransform={sanitizeMarkdownUrl}
    disallowedElements={['script', 'iframe', 'object', 'embed']}
    components={markdownComponents}
  >
    {children}
  </ReactMarkdown>
);

// Short-viewport (e.g. landscape phones ~390px tall): let the whole main section
// scroll so the notes and dismissal action stay reachable instead of being clipped.
const SHORT_SCREEN_QUERY = '@media (max-height:700px)';

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      overflow: 'hidden',
      maxWidth: 960,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100vw - 32px)',
        maxWidth: 'none',
        margin: theme.spacing(2),
      },
    },
  },
  dialogContent: {
    display: 'flex',
    padding: 0,
    height: 640,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      height: 'calc(100vh - 96px)',
    },
    [SHORT_SCREEN_QUERY]: {
      height: 'auto',
      maxHeight: 'calc(100vh - 64px)',
    },
  },
  sidebar: {
    width: 260,
    minWidth: 260,
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.default
        : theme.palette.grey[50],
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 0,
      height: 156,
      flex: '0 0 156px',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  sidebarHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  sidebarTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
  },
  releaseList: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(1),
  },
  majorGroupHeader: {
    borderRadius: 8,
    marginBottom: theme.spacing(0.25),
    padding: theme.spacing(1, 1.5),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  majorGroupHeaderSelected: {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  majorGroupLabel: {
    fontWeight: 700,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
  },
  majorGroupDate: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  majorGroupMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  releaseItem: {
    borderRadius: 6,
    marginBottom: theme.spacing(0.25),
    paddingLeft: theme.spacing(3),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  releaseItemSelected: {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  releaseItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  releaseVersion: {
    fontWeight: 500,
    fontSize: '0.8rem',
  },
  releaseDate: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  latestChip: {
    height: 18,
    fontSize: '0.6rem',
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  minorCount: {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    [SHORT_SCREEN_QUERY]: {
      overflow: 'auto',
    },
  },
  header: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    [SHORT_SCREEN_QUERY]: {
      flexShrink: 0,
    },
  },
  headerPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
    background: `radial-gradient(circle at 20% 50%, ${theme.palette.primary.contrastText} 1px, transparent 1px), radial-gradient(circle at 80% 50%, ${theme.palette.primary.contrastText} 1px, transparent 1px)`,
    backgroundSize: '40px 40px',
  },
  headerContent: {
    position: 'relative',
    zIndex: 1,
  },
  headerLogo: {
    height: 30,
    width: 'auto',
    display: 'block',
    margin: `0 auto ${theme.spacing(1)}px`,
  },
  versionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.15)'
        : 'rgba(255,255,255,0.25)',
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.5, 2),
    borderRadius: 20,
    fontWeight: 700,
    fontSize: '1rem',
    marginTop: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    opacity: 0.95,
    fontSize: '0.85rem',
    color: theme.palette.primary.contrastText,
  },
  content: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    [SHORT_SCREEN_QUERY]: {
      flex: 'none',
      overflow: 'visible',
    },
  },
  description: {
    fontSize: '0.95rem',
    lineHeight: 1.7,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  markdownSection: {
    backgroundColor: theme.palette.action.hover,
    borderRadius: 12,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    '& h1, & h2, & h3': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(1.5),
      '&:first-child': {
        marginTop: 0,
      },
    },
    '& h1': {
      fontSize: '1.3rem',
    },
    '& h2': {
      fontSize: '1.15rem',
    },
    '& h3': {
      fontSize: '1rem',
    },
    '& ul': {
      paddingLeft: theme.spacing(3),
      margin: theme.spacing(1, 0),
      listStyleType: 'disc',
    },
    '& ol': {
      paddingLeft: theme.spacing(3),
      margin: theme.spacing(1, 0),
      listStyleType: 'decimal',
    },
    '& li': {
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      fontSize: '0.95rem',
    },
    '& p': {
      marginBottom: theme.spacing(1.5),
      lineHeight: 1.7,
      color: theme.palette.text.primary,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& code': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.06)',
      color: theme.palette.type === 'dark' ? '#ff79c6' : '#c7254e',
      padding: theme.spacing(0.25, 0.75),
      borderRadius: 4,
      fontSize: '0.875em',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(0, 0, 0, 0.3)'
          : 'rgba(0, 0, 0, 0.04)',
      padding: theme.spacing(2),
      borderRadius: 8,
      overflow: 'auto',
      marginBottom: theme.spacing(2),
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        color: 'inherit',
      },
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& strong': {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    '& blockquote': {
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      paddingLeft: theme.spacing(2),
      margin: theme.spacing(2, 0),
      fontStyle: 'italic',
      color: theme.palette.text.primary,
    },
  },
  markdownTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  authorSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
  authorAvatar: {
    width: 28,
    height: 28,
  },
  authorText: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  actions: {
    padding: theme.spacing(1.5, 3),
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1),
      // Column layout: drop MUI's default left margin between stacked controls.
      '& > :not(:first-child)': {
        marginLeft: 0,
      },
    },
    [SHORT_SCREEN_QUERY]: {
      flexShrink: 0,
    },
  },
  actionsRight: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      width: '100%',
    },
  },
  primaryButton: {
    borderRadius: 25,
    padding: theme.spacing(1, 4),
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
  },
  secondaryButton: {
    borderRadius: 25,
    padding: theme.spacing(1, 3),
    textTransform: 'none',
  },
  autoPopupCheckbox: {
    marginLeft: theme.spacing(0.5),
    '& .MuiTypography-root': {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    gap: theme.spacing(2),
  },
  errorContainer: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  headerReleaseDate: {
    fontSize: '0.75rem',
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
    color: theme.palette.primary.contrastText,
  },
  showAllReleases: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    textAlign: 'center',
  },
  showAllReleasesButton: {
    textTransform: 'none',
    fontSize: '0.8rem',
    fontWeight: 500,
    borderRadius: 20,
    width: '100%',
  },
}));

type Props = {
  open: boolean;
  majorGroups: MajorReleaseGroup[];
  latestRelease: WhatsNewRelease | null;
  loading: boolean;
  error: string | null;
  hasNewVersion: boolean;
  autoPopupDisabled: boolean;
  onClose: (markAsRead: boolean) => void;
  onRefresh: () => void;
  onAutoPopupChange: (disabled: boolean) => void;
  isAdmin?: boolean;
  onGenerateSummary?: (version: string) => Promise<string>;
  onSaveAnnotation?: (
    version: string,
    input: {
      title: string | null;
      summary: string | null;
      status: 'draft' | 'published';
    },
  ) => Promise<void>;
  onFetchLatest?: () => Promise<number>;
};

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

const formatShortDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const ChangesPanel = ({
  release,
  classes,
}: {
  release: WhatsNewRelease;
  classes: Record<string, string>;
}) => (
  <>
    <Typography className={classes.description}>
      {release.description}
    </Typography>

    {release.body && release.body.trim() ? (
      <Box className={classes.markdownSection}>
        <Typography className={classes.markdownTitle}>
          <StarIcon fontSize="small" />
          What's Included
        </Typography>
        <SafeMarkdown>{release.body}</SafeMarkdown>
      </Box>
    ) : (
      <Typography
        color="textSecondary"
        style={{ textAlign: 'center', padding: 16 }}
      >
        No release notes available
      </Typography>
    )}

    {release.author.name && (
      <Box className={classes.authorSection}>
        <Avatar src={release.author.avatarUrl} className={classes.authorAvatar}>
          {release.author.name.charAt(0).toUpperCase()}
        </Avatar>
        <Typography className={classes.authorText}>
          Released by <strong>{release.author.name}</strong>
        </Typography>
      </Box>
    )}
  </>
);

type SummaryPanelProps = {
  release: WhatsNewRelease;
  isAdmin: boolean;
  classes: Record<string, string>;
  onGenerateSummary?: (version: string) => Promise<string>;
  onSaveAnnotation?: (
    version: string,
    input: {
      title: string | null;
      summary: string | null;
      status: 'draft' | 'published';
    },
  ) => Promise<void>;
};

const SummaryPanel = ({
  release,
  isAdmin,
  classes,
  onGenerateSummary,
  onSaveAnnotation,
}: SummaryPanelProps) => {
  const [title, setTitle] = useState(release.annotation?.title ?? '');
  const [summary, setSummary] = useState(release.annotation?.summary ?? '');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  const [localStatus, setLocalStatus] = useState<'draft' | 'published' | null>(
    release.annotation?.status ?? null,
  );

  const publishedTitle = release.annotation?.title;
  const publishedSummary = release.annotation?.summary;

  // Only gated (post-cutoff) releases are authorable. Legacy releases and all
  // non-admin viewers get the read-only view.
  if (!isAdmin || !release.gated) {
    if (!publishedSummary && !publishedTitle) {
      return (
        <Typography
          color="textSecondary"
          style={{ textAlign: 'center', padding: 16 }}
        >
          No summary has been published for this release yet.
        </Typography>
      );
    }
    return (
      <Box className={classes.markdownSection}>
        {publishedTitle && (
          <Typography className={classes.markdownTitle}>
            {publishedTitle}
          </Typography>
        )}
        {publishedSummary && <SafeMarkdown>{publishedSummary}</SafeMarkdown>}
      </Box>
    );
  }

  const handleGenerate = async () => {
    if (!onGenerateSummary) return;
    setFeedback(null);
    setGenerating(true);
    try {
      setSummary(await onGenerateSummary(release.version));
      setFeedback({
        severity: 'success',
        message: 'AI summary generated. Review it, then Save Draft or Publish.',
      });
    } catch (e) {
      setFeedback({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to generate summary',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (status: 'draft' | 'published') => {
    if (!onSaveAnnotation) return;
    setFeedback(null);
    setSaving(true);
    try {
      await onSaveAnnotation(release.version, {
        title: title.trim() || null,
        summary: summary.trim() || null,
        status,
      });
      setLocalStatus(status);
      setFeedback({
        severity: 'success',
        message:
          status === 'published'
            ? 'Published \u2014 this release is now visible to all users.'
            : 'Draft saved \u2014 not visible to users yet.',
      });
    } catch (e) {
      setFeedback({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to save',
      });
    } finally {
      setSaving(false);
    }
  };

  const isPublished =
    (localStatus ?? release.annotation?.status) === 'published';

  const handleUnpublish = async () => {
    if (!onSaveAnnotation) return;
    setFeedback(null);
    setSaving(true);
    try {
      await onSaveAnnotation(release.version, {
        title: title.trim() || null,
        summary: summary.trim() || null,
        status: 'draft',
      });
      setLocalStatus('draft');
      setFeedback({
        severity: 'success',
        message: 'Unpublished \u2014 this release is hidden from users again.',
      });
    } catch (e) {
      setFeedback({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to unpublish',
      });
    } finally {
      setSaving(false);
    }
  };
  // Gated + not yet published → hidden from users until the admin publishes.
  const hiddenFromUsers = Boolean(release.gated) && !isPublished;

  return (
    <Box>
      {release.gated && (
        <Typography
          variant="body2"
          style={{
            marginBottom: 12,
            color: hiddenFromUsers ? '#b26a00' : '#2e7d32',
            fontWeight: 500,
          }}
        >
          {hiddenFromUsers
            ? 'Draft — not visible to users yet. Publish to make this release appear in What\u2019s New.'
            : 'Published — visible to all users.'}
        </Typography>
      )}
      <TextField
        label="Title (optional)"
        value={title}
        onChange={e => setTitle(e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        style={{ marginBottom: 12 }}
      />
      <Box style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Button
          onClick={handleGenerate}
          disabled={generating || saving}
          variant="outlined"
          color="primary"
          startIcon={
            generating ? (
              <CircularProgress size={16} />
            ) : (
              <StarIcon fontSize="small" />
            )
          }
        >
          {generating ? 'Generating…' : 'Generate with AI'}
        </Button>
        <Button
          onClick={() => handleSave('draft')}
          disabled={saving || generating}
          variant="outlined"
          color="default"
        >
          Save Draft
        </Button>
        <Button
          onClick={() => handleSave('published')}
          disabled={saving || generating}
          variant="contained"
          color="primary"
        >
          {saving ? 'Saving…' : 'Publish'}
        </Button>
        {isPublished && (
          <Button
            onClick={handleUnpublish}
            disabled={saving || generating}
            variant="outlined"
            color="secondary"
          >
            Unpublish
          </Button>
        )}
      </Box>
      <TextField
        label="Summary (markdown)"
        value={summary}
        onChange={e => setSummary(e.target.value)}
        fullWidth
        multiline
        minRows={6}
        variant="outlined"
      />
      {summary.trim() && (
        <Box className={classes.markdownSection} style={{ marginTop: 12 }}>
          <Typography className={classes.markdownTitle}>Preview</Typography>
          <SafeMarkdown>{summary}</SafeMarkdown>
        </Box>
      )}
      <Snackbar
        open={!!feedback}
        autoHideDuration={5000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarContent
          style={{
            backgroundColor:
              feedback?.severity === 'success' ? '#2e7d32' : '#c62828',
            color: '#fff',
          }}
          message={feedback?.message ?? ''}
        />
      </Snackbar>
    </Box>
  );
};

export const WhatsNewDialog = ({
  open,
  majorGroups,
  latestRelease,
  loading,
  error,
  hasNewVersion,
  autoPopupDisabled,
  onClose,
  onRefresh,
  onAutoPopupChange,
  isAdmin = false,
  onGenerateSummary,
  onSaveAnnotation,
  onFetchLatest,
}: Props) => {
  const classes = useStyles();
  const [selectedRelease, setSelectedRelease] =
    useState<WhatsNewRelease | null>(null);
  // Track which major groups are expanded in the sidebar
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'summary' | 'changes'>('changes');
  const [fetching, setFetching] = useState(false);
  const [fetchMsg, setFetchMsg] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleFetchLatest = async () => {
    if (!onFetchLatest) return;
    setFetchMsg(null);
    setFetching(true);
    try {
      const count = await onFetchLatest();
      setFetchMsg({
        severity: 'success',
        message: `Fetched latest releases from GitHub (${count} available).`,
      });
    } catch (e) {
      setFetchMsg({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to fetch releases',
      });
    } finally {
      setFetching(false);
    }
  };

  // Set selected release and expand the latest major group when dialog opens
  useEffect(() => {
    if (open && latestRelease && !selectedRelease) {
      setSelectedRelease(latestRelease);
      // Auto-expand the major group containing the latest release
      if (majorGroups.length > 0) {
        setExpandedGroups(new Set([majorGroups[0].majorVersion]));
      }
    }
  }, [open, latestRelease, selectedRelease, majorGroups]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedRelease(null);
      setExpandedGroups(new Set());
    }
  }, [open]);

  const handleGroupClick = (group: MajorReleaseGroup) => {
    const isExpanded = expandedGroups.has(group.majorVersion);
    const nextExpandedGroups = new Set(expandedGroups);

    if (isExpanded) {
      // Collapse the group
      nextExpandedGroups.delete(group.majorVersion);
    } else {
      // Expand the group and select its latest release
      nextExpandedGroups.add(group.majorVersion);
    }

    setExpandedGroups(nextExpandedGroups);

    if (!isExpanded) {
      setSelectedRelease(group.latestRelease);
    }
  };

  const currentRelease = selectedRelease || latestRelease;
  const isLatest = currentRelease?.id === latestRelease?.id;
  const currentReleaseVersion = currentRelease?.version;
  const currentReleaseSummary = currentRelease?.annotation?.summary;
  const currentReleaseGated = currentRelease?.gated;

  // Default to Summary only when there's a published summary, or when an admin
  // can author a gated (post-cutoff) release. Legacy releases open on changes.
  useEffect(() => {
    if (!currentReleaseVersion) return;
    const hasSummary = Boolean(currentReleaseSummary);
    const canAuthor = isAdmin && Boolean(currentReleaseGated);
    setActiveTab(hasSummary || canAuthor ? 'summary' : 'changes');
  }, [
    currentReleaseVersion,
    currentReleaseSummary,
    currentReleaseGated,
    isAdmin,
  ]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
      onClose={() => onClose(false)}
    >
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
          <Typography color="textSecondary">Loading...</Typography>
        </Box>
      ) : error ? (
        <Box className={classes.errorContainer}>
          <Typography variant="h6" gutterBottom color="error">
            Failed to load
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {error}
          </Typography>
          <Box style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Button onClick={onRefresh} color="primary" variant="outlined">
              Try Again
            </Button>
            <Button onClick={() => onClose(false)} color="default">
              Close
            </Button>
          </Box>
        </Box>
      ) : majorGroups.length > 0 && currentRelease ? (
        <Box className={classes.dialogContent}>
          {/* Sidebar with major/minor release hierarchy */}
          <Box className={classes.sidebar}>
            <Box className={classes.sidebarHeader}>
              <Typography className={classes.sidebarTitle}>Releases</Typography>
              {isAdmin && (
                <Button
                  onClick={handleFetchLatest}
                  disabled={fetching}
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={
                    fetching ? <CircularProgress size={14} /> : undefined
                  }
                  style={{ marginTop: 8, textTransform: 'none' }}
                >
                  {fetching ? 'Fetching…' : 'Fetch latest release'}
                </Button>
              )}
            </Box>
            <List className={classes.releaseList} disablePadding>
              {majorGroups.map((group, groupIndex) => {
                const isGroupExpanded = expandedGroups.has(group.majorVersion);
                const isGroupActive = group.releases.some(
                  r => r.id === currentRelease.id,
                );

                return (
                  <Box key={group.majorVersion}>
                    {/* Major version header */}
                    <Box
                      className={`${classes.majorGroupHeader} ${
                        isGroupActive && !isGroupExpanded
                          ? classes.majorGroupHeaderSelected
                          : ''
                      }`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isGroupExpanded}
                      onClick={() => handleGroupClick(group)}
                      onKeyDown={(e: KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleGroupClick(group);
                        }
                      }}
                    >
                      <Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 6 }}
                        >
                          <Typography className={classes.majorGroupLabel}>
                            v{group.label}
                          </Typography>
                          {groupIndex === 0 && (
                            <Chip
                              label="Latest"
                              size="small"
                              className={classes.latestChip}
                            />
                          )}
                        </Box>
                        <Typography className={classes.majorGroupDate}>
                          {formatShortDate(group.lastReleasedAt)}
                        </Typography>
                      </Box>
                      <Box className={classes.majorGroupMeta}>
                        <Typography className={classes.minorCount}>
                          {group.releases.length} release
                          {group.releases.length !== 1 ? 's' : ''}
                        </Typography>
                        {isGroupExpanded ? (
                          <ExpandLessIcon fontSize="small" color="action" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" color="action" />
                        )}
                      </Box>
                    </Box>

                    {/* Minor releases within this major group */}
                    <Collapse in={isGroupExpanded}>
                      <List disablePadding>
                        {group.releases.map(release => (
                          <ListItem
                            key={release.id}
                            button
                            className={`${classes.releaseItem} ${
                              release.id === currentRelease.id
                                ? classes.releaseItemSelected
                                : ''
                            }`}
                            onClick={() => setSelectedRelease(release)}
                          >
                            <ListItemText
                              disableTypography
                              primary={
                                <Box className={classes.releaseItemContent}>
                                  <Box>
                                    <Typography
                                      className={classes.releaseVersion}
                                    >
                                      v{release.version}
                                    </Typography>
                                    <Typography className={classes.releaseDate}>
                                      {formatShortDate(release.publishedAt)}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </List>

            {/* Link to full releases page on GitHub */}
            <Box className={classes.showAllReleases}>
              <Button
                component="a"
                href={(() => {
                  const url = majorGroups[0]?.latestRelease?.url || '';
                  const tagIndex = url.indexOf('/releases/');
                  return tagIndex !== -1
                    ? `${url.substring(0, tagIndex)}/tags`
                    : 'https://github.com/bayer-int/backstage/tags';
                })()}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                color="primary"
                className={classes.showAllReleasesButton}
                endIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
              >
                Show all releases
              </Button>
            </Box>
          </Box>

          {/* Main content area */}
          <Box className={classes.mainContent}>
            {/* Header */}
            <Box className={classes.header}>
              <Box className={classes.headerPattern} />
              <Box className={classes.headerContent}>
                <img
                  className={classes.headerLogo}
                  src={idpLogo}
                  alt="IDP Engineers Hub"
                />
                <Typography className={classes.title}>
                  {hasNewVersion && isLatest
                    ? '🎉 New Update Available!'
                    : "What's New"}
                </Typography>
                <Typography className={classes.subtitle}>
                  {currentRelease.title}
                </Typography>
                <Box className={classes.versionBadge}>
                  <StarIcon fontSize="small" />v{currentRelease.version}
                </Box>
                <Typography className={classes.headerReleaseDate}>
                  Released on {formatDate(currentRelease.publishedAt)}
                </Typography>
              </Box>
            </Box>

            {/* Content */}
            <DialogContent className={classes.content}>
              {currentRelease.gated ||
              currentRelease.annotation?.summary ||
              currentRelease.annotation?.title ? (
                <>
                  <Tabs
                    value={activeTab}
                    onChange={(_e, value) => setActiveTab(value)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                  >
                    <Tab value="changes" label="What's Changed" />
                    <Tab value="summary" label="Summary" />
                  </Tabs>

                  <Box style={{ marginTop: 16 }}>
                    {activeTab === 'summary' ? (
                      <SummaryPanel
                        key={currentRelease.version}
                        release={currentRelease}
                        isAdmin={isAdmin}
                        classes={classes}
                        onGenerateSummary={onGenerateSummary}
                        onSaveAnnotation={onSaveAnnotation}
                      />
                    ) : (
                      <ChangesPanel
                        release={currentRelease}
                        classes={classes}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <Box style={{ marginTop: 16 }}>
                  <ChangesPanel release={currentRelease} classes={classes} />
                </Box>
              )}
            </DialogContent>

            {/* Actions */}
            <DialogActions className={classes.actions}>
              <Tooltip title="When unchecked, the popup will not appear automatically on major releases. You can still open it manually.">
                <FormControlLabel
                  className={classes.autoPopupCheckbox}
                  control={
                    <Checkbox
                      size="small"
                      checked={!autoPopupDisabled}
                      onChange={e => onAutoPopupChange(!e.target.checked)}
                      color="default"
                    />
                  }
                  label="Show automatically for major releases"
                />
              </Tooltip>
              <Box className={classes.actionsRight}>
                <Button
                  component="a"
                  href={currentRelease.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="primary"
                  className={classes.secondaryButton}
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  style={{ textDecoration: 'none' }}
                >
                  View Details
                </Button>
                <Button
                  onClick={() => onClose(true)}
                  color="primary"
                  variant="contained"
                  className={classes.primaryButton}
                >
                  {hasNewVersion ? 'Got it!' : 'Close'}
                </Button>
              </Box>
            </DialogActions>
          </Box>
        </Box>
      ) : (
        <Box className={classes.loadingContainer}>
          <Typography color="textSecondary">No releases found</Typography>
          <Box style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {isAdmin && (
              <Button
                onClick={handleFetchLatest}
                disabled={fetching}
                color="primary"
                variant="outlined"
                startIcon={
                  fetching ? <CircularProgress size={14} /> : undefined
                }
              >
                {fetching ? 'Fetching…' : 'Fetch latest release'}
              </Button>
            )}
            <Button onClick={() => onClose(false)} color="primary">
              Close
            </Button>
          </Box>
        </Box>
      )}
      <Snackbar
        open={!!fetchMsg}
        autoHideDuration={5000}
        onClose={() => setFetchMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarContent
          style={{
            backgroundColor:
              fetchMsg?.severity === 'success' ? '#2e7d32' : '#c62828',
            color: '#fff',
          }}
          message={fetchMsg?.message ?? ''}
        />
      </Snackbar>
    </Dialog>
  );
};
